import type { Rule } from "../types";
import { makeFiring } from "../util";

const EVIDENCE = "roberts_2015_cold_post_strength";

/**
 * Cold-water-immersion / ice-bath logged within 4 hours after a strength
 * session in the last 7 days. Roberts 2015: post-strength CWI blunts p70S6K
 * phosphorylation and long-term hypertrophic adaptations. Skip cold for ≥ 4h
 * post-lift, or move it to a different day.
 */
function isStrengthSport(sport_id: string): boolean {
  return /strength|gym|lift|crossfit/.test(sport_id);
}

function isColdRecovery(kind: string): boolean {
  return /cold|ice|cwi|plunge/.test(kind);
}

export const coldPostStrengthViolation: Rule = {
  id: "cold_post_strength_violation",
  domain: "health.recovery",
  topics: ["cold_exposure", "strength_adaptation"],
  evidence_refs: [EVIDENCE],
  title: () => "Cold logged within 4h of strength",
  evaluate(ctx) {
    const sevenDaysAgo = new Date(ctx.now.getTime() - 7 * 86_400_000);
    const recentSessions = ctx.sessions.filter(
      (s) => isStrengthSport(s.sport_id) && new Date(s.end_ts) >= sevenDaysAgo,
    );
    if (recentSessions.length === 0) return null;

    const violations: { session_end: string; recovery_ts: string }[] = [];
    for (const sess of recentSessions) {
      const sessEnd = new Date(sess.end_ts).getTime();
      for (const rec of ctx.recovery_sessions) {
        if (!isColdRecovery(rec.kind)) continue;
        const recTs = new Date(rec.ts).getTime();
        const dt = recTs - sessEnd;
        if (dt >= 0 && dt <= 4 * 3_600_000) {
          violations.push({ session_end: sess.end_ts, recovery_ts: rec.ts });
        }
      }
    }
    if (violations.length === 0) return null;

    return makeFiring({
      rule_id: "cold_post_strength_violation",
      now: ctx.now,
      tz: ctx.tz,
      severity: "warn",
      message: `${violations.length} cold-recovery session${violations.length === 1 ? "" : "s"} logged within 4h of a strength workout this week. Cold blunts hypertrophy adaptations in that window.`,
      evidence_refs: [EVIDENCE],
      factors: [
        {
          name: "violations_7d",
          value: violations.length,
          contribution: "delay cold ≥4h",
          evidence_ref: EVIDENCE,
        },
      ],
      target_surface: "health.recovery",
    });
  },
};
